import YAML from "yaml";
import { toString } from "mdast-util-to-string";
import { parseMdast, scan } from "./scan.js";
import { isComponentName, isTextComponent, validateComponent, COMPONENT_NAMES } from "./schemas.js";
import { buildOutline } from "./outline.js";
/** Walk a mdast tree depth-first. Returning false from `fn` skips the node's children. */
function visit(node, fn) {
    if (fn(node) === false)
        return;
    const children = node.children;
    if (children)
        for (const c of children)
            visit(c, fn);
}
const WIKILINK = /\[\[([^\]|]+?)(?:\|[^\]]*)?\]\]/g;
/** Wikilink targets from text nodes only (not code, inline code or raw HTML). */
export function collectWikilinks(nodes, into) {
    for (const node of nodes) {
        visit(node, (n) => {
            if (n.type === "code" || n.type === "inlineCode" || n.type === "html")
                return false;
            if (n.type === "text") {
                for (const m of n.value.matchAll(WIKILINK)) {
                    const target = m[1].trim();
                    if (target)
                        into.add(target);
                }
            }
        });
    }
}
/** Task items of a GFM task list, depth-first. Items without a checkbox have `checked: null`. */
export function listItemsOf(body) {
    const root = parseMdast(body);
    const out = [];
    visit(root, (n) => {
        if (n.type === "listItem") {
            const para = n.children.find((c) => c.type === "paragraph");
            const text = (para ? toString(para) : toString(n)).trim();
            out.push({ item: n, text, checked: n.checked ?? null });
        }
    });
    return out;
}
function parseChecklist(body) {
    const entries = listItemsOf(body);
    if (!entries.length)
        return { error: "body must be a GFM task list (`- [ ] item` / `- [x] item`)" };
    const items = [];
    for (const [i, e] of entries.entries()) {
        if (e.checked === null)
            return { error: `item ${i} ("${e.text.slice(0, 40)}") is not a task item: start it with \`- [ ]\` or \`- [x]\`` };
        items.push({ text: e.text, checked: e.checked });
    }
    return { items };
}
function cellRaw(body, cell) {
    const first = cell.children[0];
    const last = cell.children[cell.children.length - 1];
    const s = first?.position?.start.offset;
    const e = last?.position?.end.offset;
    if (s === undefined || e === undefined)
        return "";
    return body.slice(s, e).trim();
}
function parseTable(body) {
    const root = parseMdast(body);
    const table = root.children.find((n) => n.type === "table");
    if (!table)
        return { error: "body must be a GFM table (`| a | b |` header, `|---|---|` separator, rows)" };
    const [head, ...rest] = table.children;
    const header = head ? head.children.map((c) => cellRaw(body, c)) : [];
    const rows = rest.map((r) => r.children.map((c) => cellRaw(body, c)));
    return { header, rows };
}
/** Info-string scalars for structured components: numbers and booleans are coerced. */
function coerceScalar(value) {
    if (/^-?\d+(\.\d+)?$/.test(value))
        return Number(value);
    if (value === "true")
        return true;
    if (value === "false")
        return false;
    return value;
}
function infoProps(item, coerce) {
    const out = {};
    for (const [k, v] of Object.entries(item.info.attrs)) {
        if (k === "id")
            continue;
        out[k] = coerce ? coerceScalar(v) : v;
    }
    return out;
}
function parseYamlBody(body) {
    if (!body.trim())
        return { data: {} };
    let data;
    try {
        const doc = YAML.parseDocument(body, { prettyErrors: false });
        if (doc.errors.length)
            throw doc.errors[0];
        // Keep explicitly signed numbers (`delta: +3`) as the text the author wrote.
        YAML.visit(doc, {
            Scalar(_, node) {
                if (typeof node.value === "number" && typeof node.source === "string" && node.source.startsWith("+")) {
                    node.value = node.source;
                }
            },
        });
        data = doc.toJS();
    }
    catch (err) {
        return { error: `invalid YAML: ${err.message.split("\n")[0]}` };
    }
    if (data === null || data === undefined)
        return { data: {} };
    if (typeof data !== "object" || Array.isArray(data))
        return { error: "body must be a YAML mapping (key: value lines)" };
    return { data: data };
}
function componentBlock(item, ctx, insideColumns) {
    const raw = ctx.full.slice(item.start, item.end);
    const base = { type: "component", id: item.id, name: item.name, body: item.body, raw };
    const fail = (error, extra = {}) => {
        ctx.errors.push({ blockId: item.id, message: error });
        return { ...base, props: null, error, ...extra };
    };
    const name = item.name;
    if (!isComponentName(name)) {
        return fail(`unknown component "${name}" (known: ${COMPONENT_NAMES.join(", ")})`);
    }
    let props;
    let children;
    if (name === "columns") {
        if (insideColumns)
            return fail("columns cannot contain columns");
        props = infoProps(item, true);
        children = (item.children ?? []).map((c) => toBlock(c, ctx, true));
    }
    else if (isTextComponent(name)) {
        props = infoProps(item, false);
        if (name === "checklist") {
            const r = parseChecklist(item.body);
            if (r.error)
                return fail(`checklist: ${r.error}`);
            props.items = r.items;
        }
        else if (name === "table") {
            const r = parseTable(item.body);
            if (r.error)
                return fail(`table: ${r.error}`);
            props.header = r.header;
            props.rows = r.rows;
        }
        else if (name === "mermaid") {
            if (!item.body.trim())
                return fail("mermaid: body must contain mermaid source");
        }
        else if (name === "callout") {
            collectWikilinks(parseMdast(item.body).children, ctx.wikilinks);
        }
    }
    else {
        const r = parseYamlBody(item.body);
        if (r.error)
            return fail(`${name}: ${r.error}`);
        props = { ...infoProps(item, true), ...r.data };
    }
    const v = validateComponent(name, props);
    if (!v.ok)
        return fail(v.error, children ? { children } : {});
    const block = { ...base, props: v.props };
    if (children)
        block.children = children;
    return block;
}
function toBlock(item, ctx, insideColumns = false) {
    switch (item.kind) {
        case "heading":
            return { type: "heading", id: item.id, depth: item.depth, text: item.text, raw: ctx.full.slice(item.start, item.end) };
        case "markdown":
            collectWikilinks(item.nodes, ctx.wikilinks);
            return { type: "markdown", raw: ctx.full.slice(item.start, item.end) };
        case "component":
            return componentBlock(item, ctx, insideColumns);
    }
}
/** Parse a note into blocks. Usually called on Markdown that already went through `assignIds`. */
export function parseNote(markdown) {
    const ctx = { full: markdown, errors: [], wikilinks: new Set() };
    const blocks = scan(markdown).map((it) => toBlock(it, ctx));
    const h1 = blocks.find((b) => b.type === "heading" && b.depth === 1);
    return {
        title: h1 && h1.type === "heading" ? h1.text : null,
        blocks,
        outline: buildOutline(blocks),
        wikilinks: [...ctx.wikilinks],
        errors: ctx.errors,
    };
}
//# sourceMappingURL=parse.js.map